import { TypeGuards } from '@codeleap/common'
import { GlobalState } from '@codeleap/store'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * How long a video can go without showing progress (`onReady`, or `playedSeconds` moving)
 * and without a reported error before it's treated as stalled. Set to 3s per the "Unhandled
 * infinite video stream loading freeze" bug report (Kite Tracker), whose expected result
 * caps this at 3 seconds.
 */
export const loadStallTimeout = 3000

/** How often the deadline is checked — the overlay lands at most this late. */
const stallCheckInterval = 500

type Params<Meta, Err> = {
  metadataStore: GlobalState<Meta>
  errorStore: GlobalState<Err>
  playing: boolean
  /** Only the video on screen is watched — see below. */
  active: boolean
}

/**
 * A stuck network never raises an error, so nothing else here ever learns it happened —
 * `Placeholder` spins, or the last frame just sits there. This is a watchdog on actual
 * forward progress: past `loadStallTimeout` without the player showing any, the video is
 * treated as stalled rather than pending.
 *
 * Only `onReady` (the player can actually play, written to `metadataStore`) and
 * `playedSeconds` moving (`reportProgress`) count as progress. Watching the load alone
 * disarmed the deadline once the first bytes arrived, so a connection throttled to kbps —
 * or a rebuffer that never recovers mid-playback — froze with no overlay.
 *
 * Only while `active`: the feed pre-mounts its neighbours so they buffer ahead, and those
 * share the bandwidth with the focused item — a neighbour that hasn't loaded after 3s isn't
 * stalled, it's just not the one being watched. Leaving `active` drops any stall, so an item
 * scrolled back to doesn't greet the user with an overlay from before.
 *
 * Before `onReady` the watchdog runs regardless of `playing` (a pause over a spinner doesn't
 * make the load any less stuck); after it, only while playing — a paused video isn't
 * expected to advance.
 *
 * `attempt` keys the `ReactPlayer` (see `Immersive.tsx`) so a retry is a real remount, not a
 * repaint — the player only requests the source it was given when it mounted, so clearing
 * state without recreating it would leave the same stuck instance in place.
 */
export function useVideoStallRecovery<Meta, Err>({ metadataStore, errorStore, playing, active }: Params<Meta, Err>) {
  const [attempt, setAttempt] = useState(0)
  const [stalled, setStalled] = useState(false)

  const isReady = metadataStore.use(s => !TypeGuards.isNil(s))
  const hasError = errorStore.use(s => !TypeGuards.isNil(s))

  const lastProgressAt = useRef(Date.now())
  const lastPlayedSeconds = useRef<number | null>(null)

  const reportProgress = useCallback((playedSeconds: number) => {
    // `!==`, not `>`: `loop` wraps `playedSeconds` back to 0, and that is progress too.
    if (playedSeconds === lastPlayedSeconds.current) return
    lastPlayedSeconds.current = playedSeconds
    lastProgressAt.current = Date.now()
    // The player isn't paused under the overlay, so a stream that recovers on its own must
    // take the overlay down with it.
    setStalled(false)
  }, [])

  // A load that succeeds after the deadline already fired (no retry involved) must still
  // clear `stalled` — otherwise the overlay outlives the failure it was reporting.
  useEffect(() => {
    if (!isReady) return
    lastProgressAt.current = Date.now()
    setStalled(false)
  }, [isReady])

  useEffect(() => {
    if (!active) setStalled(false)
  }, [active])

  const watching = active && (!isReady || playing) && !hasError && !stalled

  useEffect(() => {
    if (!watching) return

    // The clock starts when watching does — time spent paused, errored or before a retry
    // isn't time the player was expected to advance.
    lastProgressAt.current = Date.now()

    const timer = setInterval(() => {
      if (Date.now() - lastProgressAt.current >= loadStallTimeout) setStalled(true)
    }, stallCheckInterval)
    return () => clearInterval(timer)
    // `attempt` restarts the clock on retry even when `watching` itself never flipped.
  }, [watching, attempt])

  const retry = useCallback(() => {
    metadataStore.set(null)
    errorStore.set(null)
    lastPlayedSeconds.current = null
    setStalled(false)
    setAttempt(a => a + 1)
  }, [metadataStore, errorStore])

  return { stalled, attempt, retry, reportProgress }
}
